import { useEffect, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, Share2, Eye, TrendingUp, Award, Trophy, BookOpen, Users,
} from "lucide-react";
import { Link } from "react-router-dom";

const KIND_META = {
  badge:       { label: "Badge",       Icon: Award,    accent: "from-amber-500 to-orange-500" },
  exam_result: { label: "Exam Result", Icon: BookOpen, accent: "from-emerald-500 to-teal-500" },
  mock_exam:   { label: "Mock Exam",   Icon: Trophy,   accent: "from-violet-500 to-fuchsia-500" },
};

export default function AdminShareAnalyticsPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/share-analytics");
        setData(r.data);
      } catch (e) {
        toast.error(extractErrorMessage(e, "Couldn't load share analytics"));
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  if (busy) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (!data) return null;

  const total = data.total_shares || 0;
  return (
    <div className="space-y-6" data-testid="share-analytics-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Share2 className="w-5 h-5 text-indigo-600" /> Share Analytics
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          How students are sharing their progress — and which artifacts drive the most page views.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard
          label="Total Shares"
          value={total}
          Icon={Share2}
          tint="indigo"
          testid="summary-total-shares"
        />
        <SummaryCard
          label="Total Views"
          value={data.total_views || 0}
          Icon={Eye}
          tint="emerald"
          testid="summary-total-views"
        />
        <SummaryCard
          label="Avg Views / Share"
          value={total > 0 ? (data.total_views / total).toFixed(1) : "0"}
          Icon={TrendingUp}
          tint="amber"
          testid="summary-avg-views"
        />
        <SummaryCard
          label="Unique Sharers"
          value={data.top_sharers?.length || 0}
          Icon={Users}
          tint="rose"
          testid="summary-unique-sharers"
        />
      </div>

      {/* By kind */}
      <Card className="p-5" data-testid="by-kind-card">
        <h2 className="font-display font-bold text-base mb-3">Breakdown by Type</h2>
        {(data.by_kind?.length || 0) === 0 ? (
          <p className="text-sm text-slate-500">No shares yet.</p>
        ) : (
          <div className="space-y-3">
            {data.by_kind.map((row) => {
              const meta = KIND_META[row.kind] || { label: row.kind, Icon: Share2, accent: "from-slate-500 to-slate-600" };
              const sharePct = total > 0 ? (row.shares / total) * 100 : 0;
              const Icon = meta.Icon;
              return (
                <div key={row.kind} data-testid={`by-kind-row-${row.kind}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                      <Icon className="w-4 h-4 text-slate-500" /> {meta.label}
                    </span>
                    <span className="text-slate-500 tabular-nums text-xs">
                      <strong className="text-slate-900 dark:text-white">{row.shares}</strong> shares · {row.views} views
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${meta.accent} transition-all`}
                      style={{ width: `${sharePct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Top sharers */}
      <Card className="p-5" data-testid="top-sharers-card">
        <h2 className="font-display font-bold text-base mb-3">Top Sharers</h2>
        {(data.top_sharers?.length || 0) === 0 ? (
          <p className="text-sm text-slate-500">No data yet — the first student share will appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-1.5 pr-3">#</th>
                  <th className="py-1.5 pr-3">Handle</th>
                  <th className="py-1.5 pr-3">Email</th>
                  <th className="py-1.5 pr-3 text-right">Shares</th>
                  <th className="py-1.5 pr-3 text-right">Views</th>
                </tr>
              </thead>
              <tbody>
                {data.top_sharers.map((s, i) => (
                  <tr
                    key={s.student_id}
                    className="border-t border-slate-100 dark:border-slate-800"
                    data-testid={`top-sharer-row-${i}`}
                  >
                    <td className="py-2 pr-3 text-slate-500 tabular-nums">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium text-slate-900 dark:text-slate-100">{s.display_handle}</td>
                    <td className="py-2 pr-3 text-slate-500 truncate max-w-[220px]">{s.email}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-bold">{s.shares}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{s.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent shares */}
      <Card className="p-5" data-testid="recent-shares-card">
        <h2 className="font-display font-bold text-base mb-3">Recent Shares</h2>
        {(data.recent?.length || 0) === 0 ? (
          <p className="text-sm text-slate-500">Nothing here yet.</p>
        ) : (
          <ul className="space-y-2">
            {data.recent.map((r) => {
              const meta = KIND_META[r.kind] || { label: r.kind, Icon: Share2, accent: "from-slate-500 to-slate-600" };
              const Icon = meta.Icon;
              const url = `/share/${r.kind === "exam_result" ? "exam" : r.kind === "mock_exam" ? "mock-exam" : "badge"}/${r.share_token}`;
              return (
                <li
                  key={r.share_token}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  data-testid={`recent-share-${r.share_token}`}
                >
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${meta.accent} grid place-items-center text-white shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
                      {r.headline}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      by <strong>{r.display_handle}</strong> · {r.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0">
                    <Eye className="w-3 h-3 mr-1" /> {r.view_count}
                  </Badge>
                  <Link
                    to={url}
                    target="_blank"
                    className="text-[11px] text-indigo-600 hover:underline shrink-0"
                  >
                    Open ↗
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, Icon, tint, testid }) {
  const tintMap = {
    indigo:  "from-indigo-500 to-indigo-600",
    emerald: "from-emerald-500 to-emerald-600",
    amber:   "from-amber-500 to-amber-600",
    rose:    "from-rose-500 to-rose-600",
  };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${tintMap[tint]} grid place-items-center text-white shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
          <div className="text-xl font-display font-extrabold tabular-nums">{value}</div>
        </div>
      </div>
    </Card>
  );
}
